import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { launchGameEngine, getGameImage, getGameCurrencies } from 'hooks/useGameEngine';
import { toast } from 'react-toastify';
import {
  MdFullscreen, MdKeyboardArrowDown, MdClose
} from 'react-icons/md';
import { BsShieldCheck } from 'react-icons/bs';
import { FaPlay } from 'react-icons/fa';
import apiClient from 'services/apiClient';
import { Event } from 'utils/Helper';

// ─── Insufficient Balance Modal Styled Components ───
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background-color: #17181b;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  width: 90%;
  max-width: 370px;
  padding: 18px 20px;
  box-shadow: 0 15px 35px rgba(0,0,0,0.6);
  color: #fff;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 800;
  margin-bottom: 12px;
  color: #fff;
`;

const ModalDesc = styled.p`
  font-size: 13px;
  color: #9fafb2;
  line-height: 1.45;
  margin-bottom: 8px;
`;

const ModalSubDesc = styled.p`
  font-size: 12px;
  color: #67707b;
  line-height: 1.35;
  margin-bottom: 18px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  background-color: #1fc16b;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const SecondaryBtn = styled.button`
  background-color: #262c2d;
  color: #fff;
  border: 1px solid #3f494b;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover { background-color: #343c3e; }
`;

const CancelBtn = styled.button`
  background: none;
  border: none;
  color: #9fafb2;
  font-size: 13px;
  cursor: pointer;
  margin-top: 8px;
  &:hover { color: #fff; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
  color: #fff;
  @media (max-width: 991px) { padding: 15px; }
`;

const DesktopPlayerWrapper = styled.div`
  background-color: #000;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  border: 1px solid rgba(255,255,255,0.07);
  @media (max-width: 991px) { margin-top: 15px; }
`;

const PlayerAspect = styled.div`
  width: 100%;
  padding-top: 56.25%;
  position: relative;
  @media (max-width: 991px) {
    padding-top: 140%;
  }
`;

const GameIframe = styled.iframe`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  border: none;
  background-color: #000;
`;

// ─── Play Now Overlay ─────────────────────────────────────────────────────────

const PlayOverlay = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0d0d12 0%, #13141a 60%, #1a1c25 100%);
  gap: 28px;
  z-index: 10;
`;

const GameThumbInOverlay = styled.div`
  width: 90px;
  height: 120px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.6);
  border: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;

  img {
    width: 100%; height: 100%;
    object-fit: cover;
  }
`;

const OverlayGameTitle = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.5px;
`;

const OverlayMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-top: 6px;
`;

const CurrencyDropdownWrapper = styled.div`
  position: relative;
`;

const CurrencyDropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  padding: 10px 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s;
  min-width: 160px;
  justify-content: space-between;
  text-transform: uppercase;

  &:hover {
    background: rgba(255,255,255,0.1);
    border-color: rgba(255,255,255,0.2);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: #1e2028;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  overflow: hidden;
  z-index: 50;
  box-shadow: 0 -8px 24px rgba(0,0,0,0.5);
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: ${p => p.active ? 'rgba(31,193,107,0.15)' : 'none'};
  border: none;
  color: ${p => p.active ? '#1fc16b' : '#cbd5e1'};
  font-size: 13px;
  font-weight: ${p => p.active ? '700' : '500'};
  cursor: pointer;
  transition: 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover { background: rgba(255,255,255,0.05); }
`;

const PlayNowBtn = styled.button`
  height: 54px;
  padding: 0 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #1fc16b, #17a85c);
  color: #fff;
  border: none;
  font-weight: 900;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 6px 24px rgba(31, 193, 107, 0.35);
  transition: 0.2s;

  &:hover {
    background: linear-gradient(135deg, #25d977, #1fc16b);
    box-shadow: 0 8px 30px rgba(31, 193, 107, 0.5);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg { font-size: 16px; }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #0d0d12;
  color: #fff;
  font-weight: 800;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 2px;
  gap: 20px;
  z-index: 10;

  .spinner {
    width: 40px; height: 40px;
    border: 3px solid rgba(255,255,255,0.1);
    border-top-color: #1fc16b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin { 100% { transform: rotate(360deg); } }
`;

// ─── Toolbar ──────────────────────────────────────────────────────────────────

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 52px;
  background-color: #1b1d24;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 8px;
  padding: 8px 16px;
  margin-top: 14px;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 12px;
  }
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
  }
`;

const IconButton = styled.button`
  width: 36px; height: 36px;
  border-radius: 6px;
  background: none;
  border: none;
  color: #6b7280;
  display: grid;
  place-items: center;
  font-size: 20px;
  cursor: pointer;
  transition: 0.2s;
  &:hover { background: rgba(255,255,255,0.05); color: #fff; }
`;

const GameTitleBar = styled.div`
  font-weight: 800;
  font-size: 14px;
  color: #e2e8f0;
`;

const ProviderTag = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SecureBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #1fc16b;
  font-weight: 700;
`;

const FunRealToggle = styled.div`
  display: flex;
  align-items: center;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 20px;
  padding: 0 4px;
  height: 36px;

  span {
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 0 10px;
    color: #6b7280;
    transition: 0.3s;
  }

  .fun { color: ${props => props.isReal ? '#6b7280' : '#1fc16b'}; }
  .real { color: ${props => props.isReal ? '#1fc16b' : '#6b7280'}; }
`;

const PlaySwitch = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;

  input { display: none; }

  .toggle {
    width: 36px; height: 18px;
    background: #2a2d38;
    border-radius: 9px;
    position: relative;
    transition: 0.3s;

    &::after {
      content: '';
      position: absolute;
      width: 14px; height: 14px;
      border-radius: 50%;
      background: #6b7280;
      top: 2px; left: 2px;
      transition: 0.3s;
    }
  }

  input:checked + .toggle {
    background: #1fc16b;
    &::after { left: 20px; background: #fff; }
  }
`;

// ─── Toolbar Currency Pill ─────────────────────────────────────────────────────

const CurrencyPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #1fc16b;
  text-transform: uppercase;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function GamePlayer() {
  const { id } = useParams();
  const [iframeUrl, setIframeUrl] = useState('');
  const [gameMeta, setGameMeta] = useState(null);
  const [gameStarted, setGameStarted] = useState(false); // controls overlay vs iframe
  const [launching, setLaunching] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);

  const [isRealPlay, setIsRealPlay] = useState(true);
  const [currency, setCurrency] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const playerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch game meta + available currencies on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const loadGameInfo = async () => {
      try {
        const res = await getGameCurrencies(id);
        // res is the full response object { success, data: currencies[], game: {...} }
        if (res && res.game) {
          setGameMeta(res.game);
        }
        const currencyList = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
        if (currencyList && currencyList.length > 0) {
          setAvailableCurrencies(currencyList);
          setCurrency(currencyList[0].code.toUpperCase());
        }
      } catch (err) {
        console.error('Failed to load game info:', err);
      }
    };
    loadGameInfo();
  }, [id]);

  const handlePlayNow = async () => {
    const sessionStr = Cookies.get('session');
    let uid = Cookies.get('uid');

    if (sessionStr && !uid) {
      try {
        let base64Url = sessionStr.split('.')[1];
        let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        let pad = base64.length % 4;
        if (pad) {
          base64 += '='.repeat(4 - pad);
        }
        const decoded = JSON.parse(atob(base64));
        if (decoded && decoded.id) {
          uid = decoded.id;
        }
      } catch (e) {
        console.error('Failed to parse session token', e);
      }
    }

    if (!sessionStr) {
      toast.error('Please login to play');
      return;
    }

    if (!uid) {
      toast.error('Session not ready, please refresh the page.');
      return;
    }

    // Verify wallet balance first
    try {
      const profileRes = await apiClient.get(`/api/myprofile/${uid}`);
      if (profileRes.data && profileRes.data.status && profileRes.data.data?.[0]) {
        const user = profileRes.data.data[0];
        const userBalances = user.credit || {};
        const currentCurrency = String(currency || '').toLowerCase();
        const userBalance = parseFloat(userBalances[currentCurrency] || 0);
        
        if (userBalance <= 0) {
          setShowInsufficientModal(true);
          return;
        }
      }
    } catch (err) {
      console.error('Failed to verify balance:', err);
    }

    setLaunching(true);
    try {
      const res = await launchGameEngine(id, uid || '1', currency);
      if (res.success) {
        if (res.game) setGameMeta(res.game);

        let launch_url = res.data?.launch_url || res.data;
        if (typeof res.data === 'object' && res.data.content?.game) {
          launch_url = res.data.content.game.url;
        } else if (typeof res.data === 'object' && res.data.game_url) {
          launch_url = res.data.game_url;
        }

        if (launch_url && typeof launch_url === 'string') {
          setIframeUrl(launch_url);
          setGameStarted(true);
        } else {
          toast.error('Failed to get game URL');
        }
      } else {
        toast.error(res.error || 'Launch failed');
      }
    } catch (err) {
      console.error('Launch error:', err);
      toast.error('Failed to launch game');
    } finally {
      setLaunching(false);
    }
  };

  const toggleFullscreen = () => {
    const el = playerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(err => console.error(err));
    } else {
      document.exitFullscreen?.();
    }
  };

  const gameTitle = gameMeta?.name || 'Loading...';
  const gameThumb = gameMeta ? getGameImage(gameMeta) : '/assets/bcimages/Casino/BC_Originals.png';
  const providerName = gameMeta?.provider_name || '';

  const selectedCurrencyObj = availableCurrencies.find(c => c.code.toUpperCase() === currency);

  return (
    <>
    <PageContainer>

      {/* ── Player ── */}
      <DesktopPlayerWrapper ref={playerRef}>
        <PlayerAspect>

          {/* Play Now Overlay (before game starts) */}
          {!gameStarted && !launching && (
            <PlayOverlay>

              <div style={{ textAlign: 'center' }}>
                <OverlayGameTitle>{gameTitle}</OverlayGameTitle>
                {providerName && <OverlayMeta>{providerName}</OverlayMeta>}
              </div>



              <PlayNowBtn onClick={handlePlayNow} disabled={!currency && availableCurrencies.length > 0}>
                <FaPlay /> Play Now
              </PlayNowBtn>
            </PlayOverlay>
          )}

          {/* Launching spinner */}
          {launching && (
            <LoadingOverlay>
              <div className="spinner"></div>
              <span>Initializing Secure Session...</span>
            </LoadingOverlay>
          )}

          {/* Game Iframe */}
          {gameStarted && iframeUrl && (
            <GameIframe
              src={iframeUrl}
              allowFullScreen
              title={gameTitle}
            />
          )}

        </PlayerAspect>
      </DesktopPlayerWrapper>

      {/* ── Toolbar ── */}
      <Toolbar>
        <ToolGroup>
          <IconButton onClick={toggleFullscreen} title="Fullscreen">
            <MdFullscreen />
          </IconButton>
          {/* Currency Dropdown in toolbar */}
          {availableCurrencies.length > 0 && !gameStarted && (
            <CurrencyDropdownWrapper ref={dropdownRef} style={{ position: 'relative' }}>
              <CurrencyDropdownButton onClick={() => setDropdownOpen(o => !o)} style={{ height: 36, fontSize: 13, padding: '0 12px' }}>
                <span>💰 {currency || 'Select'}</span>
                <MdKeyboardArrowDown style={{ fontSize: 16, flexShrink: 0, transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
              </CurrencyDropdownButton>
              {dropdownOpen && (
                <DropdownMenu style={{ bottom: '100%', top: 'auto', marginBottom: 4 }}>
                  {availableCurrencies.map(c => (
                    <DropdownItem
                      key={c.code}
                      active={c.code.toUpperCase() === currency}
                      onClick={() => { setCurrency(c.code.toUpperCase()); setDropdownOpen(false); }}
                    >
                      {c.code.toUpperCase()} {c.name ? `— ${c.name}` : ''}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              )}
            </CurrencyDropdownWrapper>
          )}
          <div>
            <GameTitleBar>{gameTitle}</GameTitleBar>
            {providerName && <ProviderTag>{providerName}</ProviderTag>}
          </div>
        </ToolGroup>

        <ToolGroup>
          <SecureBadge>
            <BsShieldCheck /> Provably Fair
          </SecureBadge>
          {gameStarted && currency && (
            <CurrencyPill>💰 {currency}</CurrencyPill>
          )}
          {gameStarted && (
            <PlayNowBtn
              style={{ height: 36, padding: '0 16px', fontSize: 12, boxShadow: 'none', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' }}
              onClick={() => { setGameStarted(false); setIframeUrl(''); }}
            >
              <MdClose style={{ fontSize: 14 }} /> Exit
            </PlayNowBtn>
          )}
        </ToolGroup>
      </Toolbar>

    </PageContainer>
      {showInsufficientModal && (
        <ModalBackdrop onClick={() => setShowInsufficientModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Insufficient Wallet Balance</ModalTitle>
            <ModalDesc>
              You don't have enough balance in your {currency || 'USD'} Wallet to play this game.
            </ModalDesc>
            <ModalSubDesc>
              Deposit funds into your {currency || 'USD'} Wallet to start playing.
              If you already have funds in another wallet, swap them to your {currency || 'USD'} Wallet.
            </ModalSubDesc>
            <ButtonRow>
              <PrimaryBtn onClick={() => {
                setShowInsufficientModal(false);
                Event.emit("open_wallet_with_tab", "deposit");
              }}>
                Deposit
              </PrimaryBtn>
              <SecondaryBtn onClick={() => {
                setShowInsufficientModal(false);
                Event.emit("open_wallet_with_tab", "swap");
              }}>
                Swap to {currency || 'USD'} Wallet
              </SecondaryBtn>
              <CancelBtn onClick={() => setShowInsufficientModal(false)}>
                Cancel
              </CancelBtn>
            </ButtonRow>
          </ModalContainer>
        </ModalBackdrop>
      )}
    </>
  );
}
